import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef } from 'react';

import { Button, useUiLabel } from '@canvas-logic/clp-shared-ui';

import { useBootstrapper } from '@/ui-kit/contexts/BootstrapperContext';
import { useSheetRouter } from '@/ui-kit/components/SheetRouter/SheetRouterContext';
import { useMediaQuery } from '@/ui-kit/hooks/use-media-query';
import { ModalName, modalStore } from '@/ui-kit/components/Modal/ModalStore';
import { Modal } from '@/ui-kit/components/Modal/Modal';
import { ContactUsService } from '@/ui-kit/services/ContactUsService';

import { RequestQuote } from './RequestQuote';

import styles from './RequestQuote.module.css';

interface RequestQuoteButtonProps {
  contactUsService: ContactUsService;
}

export const RequestQuoteButton = observer(function RequestQuoteButton({ contactUsService }: RequestQuoteButtonProps) {
  const titleLabel = useUiLabel('ui.requestQuote.title');
  const submitButtonLabel = useUiLabel('ui.requestQuote.submitButton');
  const contactButtonLabel = useUiLabel('ui.requestQuote.contactButton');
  const { bootstrapper } = useBootstrapper();
  const { getIsSheetOpen, close } = useSheetRouter();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { openContactUs: openContactUsSheet } = useSheetRouter();

  const isDesktop = useMediaQuery('only screen and (min-width: 601px)');

  const handleRequestQuoteClick = useCallback(() => {
    contactUsService.resetStatus();

    if (isDesktop) {
      modalStore.openModal(ModalName.CONTACT_US, () => {
        buttonRef.current?.focus();
      });
    } else {
      openContactUsSheet();
    }
  }, [contactUsService, isDesktop, openContactUsSheet]);

  useEffect(() => {
    if (!isDesktop && modalStore.getIsOpened(ModalName.CONTACT_US)) {
      modalStore.closeModal();
      openContactUsSheet();
    }

    const openedSheetId = getIsSheetOpen('contact-us');

    if (isDesktop && openedSheetId) {
      close(openedSheetId);
      modalStore.openModal(ModalName.CONTACT_US);
    }
  }, [close, getIsSheetOpen, isDesktop, openContactUsSheet]);

  const featureSchema = bootstrapper?.contactUsService?.schema?.configuration;
  const featureButtonLabel = featureSchema?.featureNameKey === 'contactUs' ? contactButtonLabel : submitButtonLabel;

  return (
    <>
      {modalStore.getIsOpened(ModalName.CONTACT_US) && (
        <Modal modalName={ModalName.CONTACT_US} title={titleLabel} className={styles.modal}>
          <RequestQuote contactUsService={contactUsService} createLink={bootstrapper?.createLink} />
        </Modal>
      )}
      <Button data-request-button ref={buttonRef} onClick={handleRequestQuoteClick} variant="primary">
        {featureButtonLabel}
      </Button>
    </>
  );
});
